import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Last updated: March 16, 2026
          </p>

          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                By accessing or using Countr, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service. We reserve the right to modify these terms at any time, and your continued use of the service constitutes acceptance of any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                2. Description of Service
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Countr is a countdown timer application that allows users to track important events, birthdays, holidays, and special moments. The service includes the following features:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>Create and manage countdown timers</li>
                <li>Calendar view of events</li>
                <li>Email reminders for upcoming events</li>
                <li>Share countdowns via unique links</li>
                <li>Customization with emojis, colors, and images</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                3. Account Registration and Security
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                To use Countr, you must create an account by providing a valid email address and password. You are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Ensuring the accuracy of the information you provide</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                4. Subscription Tiers
              </h2>
              <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Free Plan</h3>
                  <p>The Free plan includes up to 5 countdowns, live countdown timers, calendar view, emojis and colors, and basic reminders. This plan is free forever and does not require payment information.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Pro Plan</h3>
                  <p>The Pro plan includes unlimited countdowns, custom photos and backgrounds, advanced reminders, sharing capabilities, recurring countdowns, and priority support. Pro subscriptions are available monthly or yearly.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                5. Payment and Billing
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                All payments are processed securely through Stripe, our third-party payment processor. By subscribing to a Pro plan, you agree to the following:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>Monthly subscriptions are billed every month on the date you subscribed</li>
                <li>Yearly subscriptions are billed annually on the date you subscribed</li>
                <li>You authorize us to charge your payment method for recurring subscription fees</li>
                <li>All fees are in U.S. Dollars unless otherwise stated</li>
                <li>You are responsible for any taxes, duties, or fees associated with your subscription</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                6. Cancellation and Refunds
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You may cancel your Pro subscription at any time through the Stripe Customer Portal accessible from your account settings. Upon cancellation:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>Your subscription will remain active until the end of the current billing period</li>
                <li>You will not be charged for subsequent billing periods</li>
                <li>You will retain Pro features until the end of your billing period</li>
                <li>No refunds are provided for partial billing periods</li>
                <li>We reserve the right to offer refunds on a case-by-case basis at our sole discretion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                7. User Responsibilities
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Violate any laws in your jurisdiction</li>
                <li>Infringe on the intellectual property rights of others</li>
                <li>Transmit any viruses, malware, or harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the service or servers</li>
                <li>Use automated systems to access the service without permission</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                8. Intellectual Property
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                All content, features, and functionality of Countr, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are the exclusive property of Countr and are protected by United States and international copyright, trademark, and other intellectual property laws. You retain ownership of the content you create using our service, including countdown names, descriptions, and uploaded images.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                9. Limitation of Liability
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                To the fullest extent permitted by law, Countr and its affiliates, officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, or goodwill, arising out of or related to your use of the service. Our total liability to you for any claim arising from your use of the service shall not exceed the amount you paid us in the twelve months prior to the event giving rise to the liability, or $100, whichever is greater.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                10. Termination
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We reserve the right to suspend or terminate your account and access to the service at our sole discretion, without notice, for conduct that we believe violates these Terms of Service, is harmful to other users, us, or third parties, or is otherwise objectionable. Upon termination, your right to use the service will immediately cease, and we may delete your account and all associated data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                11. Governing Law and Dispute Resolution
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                These Terms of Service shall be governed by and construed in accordance with the laws of the State of New Jersey, United States, without regard to its conflict of law provisions. Any disputes arising from these terms or your use of the service shall be resolved exclusively in the state or federal courts located in New Jersey, and you consent to the personal jurisdiction of such courts.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                12. Changes to Terms
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. We will notify users of any material changes by posting the updated terms on this page and updating the "Last updated" date. Your continued use of the service after such changes constitutes your acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                13. Contact Information
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at support@countr.app
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}